/**
 * pf-collection.js
 * Collection page interactivity — filter fetch-swap, sort, load more, URL stickiness.
 *
 * Pattern matches existing pf-*.js files:
 *   - IIFE wrapper
 *   - debounce helper
 *   - DOMContentLoaded guard
 *   - sessionStorage saves current filter URL for product page back-nav
 *
 * Loaded with defer in theme.liquid alongside other pf-*.js files.
 */

(function () {
  'use strict';

  // Only run on collection pages
  if (!window.location.pathname.includes('/collections/')) return;

  // ── DOM refs ──
  var filtersWrap  = document.getElementById('pf-filters-wrap');
  var productGrid  = document.getElementById('pf-product-grid');
  var countText    = document.getElementById('pf-count-text');
  var sortToggle   = document.getElementById('pf-sort-toggle');
  var sortDropdown = document.getElementById('pf-sort-dropdown');
  var sortLabel    = document.getElementById('pf-sort-label');
  var filterRow    = document.getElementById('pf-filter-row');
  var pagination   = document.getElementById('pf-pagination');

  // ── Prefetch next page silently after load ──
  function prefetchNextPage() {
    var pagination = document.getElementById('pf-pagination');
    if (!pagination) return;
    var loadMoreBtn = document.getElementById('pf-load-more');
    if (!loadMoreBtn) return;
    var nextUrl = loadMoreBtn.getAttribute('data-next-url');
    if (!nextUrl) return;

    // Use link prefetch hint first (zero cost)
    var hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = nextUrl;
    hint.as = 'document';
    document.head.appendChild(hint);

    // Also fetch and cache in memory for instant swap
    fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function(r) { return r.text(); })
      .then(function(html) {
        loadMoreBtn.dataset.prefetchedHtml = '';
        window.pfPrefetchCache = window.pfPrefetchCache || {};
        window.pfPrefetchCache[nextUrl] = html;
      })
      .catch(function() {});
  }

  // Fire prefetch after page is idle
  if (window.requestIdleCallback) {
    requestIdleCallback(prefetchNextPage, { timeout: 3000 });
  } else {
    setTimeout(prefetchNextPage, 2000);
  }

  // ── Save current URL to sessionStorage — must run before guard ──
  function saveFilterUrl() {
    try {
      sessionStorage.setItem('pf_collection_url', window.location.href);
      var url = new URL(window.location.href);
      var activeFilters = {};
      url.searchParams.forEach(function(v, k) {
        if (k.startsWith('filter.')) activeFilters[k] = v;
      });
      if (Object.keys(activeFilters).length) {
        sessionStorage.setItem('pf_active_filters', JSON.stringify(activeFilters));
      } else {
        sessionStorage.removeItem('pf_active_filters');
      }
    } catch (e) {}
  }
  saveFilterUrl();

  if (!filtersWrap || !productGrid) return;

  // ── Initial state from Liquid data island ──
  var dataEl = document.getElementById('pf-filter-data');
  var state  = dataEl ? JSON.parse(dataEl.textContent) : {};

  // ─────────────────────────────────────────────
  //  PRICE SORT TOGGLE
  // ─────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#pf-sort-toggle');
    if (!btn) return;

    e.preventDefault();

    var currentParams = getCurrentParams();
    var currentSort = currentParams['sort_by'];

    var nextSort =
      currentSort === 'price-ascending'
        ? 'price-descending'
        : 'price-ascending';

    applySort(nextSort);
  });

  var SORT_LABELS = {
    'price-ascending':  'Price ↓',
    'price-descending': 'Price ↑'
  };

  function applySort(sortValue) {
    var params = getCurrentParams();
    params['sort_by'] = sortValue;
    var url = buildUrl(params);
    fetchSwap(url, function () {
      var freshSortLabel = document.getElementById('pf-sort-label');
      var freshSortToggle = document.getElementById('pf-sort-toggle');

      if (freshSortLabel) {
        freshSortLabel.innerHTML =
          sortValue === 'price-ascending' ? 'Price ↓' : 'Price ↑';
      }

      if (freshSortToggle) {
        freshSortToggle.setAttribute(
          'data-next-sort',
          sortValue === 'price-ascending'
            ? 'price-descending'
            : 'price-ascending'
        );
      }

      var url = new URL(window.location.href);

      if (sortValue === 'price-ascending') {
        url.searchParams.set('sort_by', 'price-ascending');
      } else {
        url.searchParams.set('sort_by', 'price-descending');
      }

      history.replaceState({}, '', url.toString());
    });
  }

  // ─────────────────────────────────────────────
  //  FILTER MODAL
  // ─────────────────────────────────────────────
  document.addEventListener('click', function (e) {

    var openBtn = e.target.closest('#pf-open-filters');
    var closeBtn = e.target.closest('#pf-filter-modal-close, #pf-filter-overlay');
    var modalPill = e.target.closest('.pf-filter-modal__pill');
    var clearBtn = e.target.closest('#pf-clear-all');

    // OPEN
    if (openBtn) {
      e.preventDefault();
      document.documentElement.classList.add('pf-filter-modal-open');
      return;
    }

    // CLOSE
    if (closeBtn) {
      e.preventDefault();
      document.documentElement.classList.remove('pf-filter-modal-open');
      return;
    }

    // CLEAR
    if (clearBtn) {
      e.preventDefault();
      clearAllPills();
      fetchSwap(buildUrl({}));
      return;
    }

    // MODAL CLEAR
    var modalClearBtn = e.target.closest('#pf-filter-clear-btn');
    if (modalClearBtn) {
      e.preventDefault();
      clearAllPills();
      fetchSwap(buildUrl({}));
      document.documentElement.classList.remove('pf-filter-modal-open');
      return;
    }

    // MODAL SHOW
    var modalShowBtn = e.target.closest('#pf-filter-show-btn');
    if (modalShowBtn) {
      e.preventDefault();
      document.documentElement.classList.remove('pf-filter-modal-open');
      return;
    }

    // FILTER CLICK
    if (modalPill) {

      e.preventDefault();
      e.stopPropagation();

      if (
        modalPill.disabled ||
        modalPill.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      var param = modalPill.getAttribute('data-filter-param');
      var value = modalPill.getAttribute('data-filter-value');
      var isActive = modalPill.getAttribute('data-active') === 'true';

      var newParams = getCurrentParams();

      if (isActive) {
        delete newParams[param];
        modalPill.classList.remove('is-active');
        modalPill.setAttribute('data-active', 'false');
      } else {
        newParams[param] = value;
        modalPill.classList.add('is-active');
        modalPill.setAttribute('data-active', 'true');
      }

      applyFilters(newParams);

      setTimeout(function () {
        document.documentElement.classList.add('pf-filter-modal-open');
      }, 50);

      return;
    }

  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.documentElement.classList.remove('pf-filter-modal-open');
    }
  });

  function clearAllPills() {
    document.querySelectorAll('.pf-filter-modal__pill').forEach(function (pill) {
      pill.classList.remove('is-active');
      pill.setAttribute('data-active', 'false');
      pill.setAttribute('aria-pressed', 'false');
    });
  }

  function applyFilters(filterParams) {
    // Carry over existing active filters + sort
    var urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach(function (v, k) {
      if (k === 'sort_by' || k.startsWith('filter.')) {
        if (!(k in filterParams)) filterParams[k] = v;
      }
    });
    fetchSwap(buildUrl(filterParams));
  }

  // ─────────────────────────────────────────────
  //  URL HELPERS
  // ─────────────────────────────────────────────
  function getCurrentParams() {
    var params = {};
    var urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach(function (v, k) {
      if (k.startsWith('filter.') || k === 'sort_by') params[k] = v;
    });
    return params;
  }

  function buildUrl(params) {
    var url = new URL(window.location.href);
    // Clear existing filter + sort + page
    var toDelete = [];
    url.searchParams.forEach(function (v, k) {
      if (k.startsWith('filter.') || k === 'sort_by' || k === 'page') toDelete.push(k);
    });
    toDelete.forEach(function (k) { url.searchParams.delete(k); });
    // Re-apply
    Object.keys(params).forEach(function (k) { url.searchParams.set(k, params[k]); });
    return url.toString();
  }

  // ─────────────────────────────────────────────
  //  FETCH SWAP — core engine
  // ─────────────────────────────────────────────
  function fetchSwap(url, afterCb) {
    setLoading(true);
    history.pushState({ pf: true }, '', url);
    saveFilterUrl();

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        swapEl('pf-product-grid',    doc);
        swapEl('pf-count-text',      doc);
        swapEl('pf-filter-row',      doc);
        // swapEl('pf-filter-modal', doc); // managed client-side
        swapEl('pf-pagination',      doc);
        swapEl('pf-filter-data',     doc);

        // Keep current scroll position

        if (afterCb) afterCb();
        setLoading(false);
      })
      .catch(function (err) {
        console.warn('[pf-collection] fetch failed, navigating:', err);
        window.location.href = url;
      });
  }

  function swapEl(id, doc) {
    var newEl = doc.getElementById(id);
    var oldEl = document.getElementById(id);
    if (newEl && oldEl) oldEl.innerHTML = newEl.innerHTML;
  }

  // ─────────────────────────────────────────────
  //  LOAD MORE — appends products, no scroll reset
  // ─────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#pf-load-more');
    if (!btn) return;
    var nextUrl = btn.getAttribute('data-next-url');
    if (!nextUrl) return;
    btn.disabled = true;
    btn.textContent = 'Loading\u2026';

    // Use prefetch cache if available for instant load
    var cached = window.pfPrefetchCache && window.pfPrefetchCache[nextUrl];
    var fetchPromise = cached
      ? Promise.resolve(cached)
      : fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function(r) { return r.text(); });

    fetchPromise
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.getElementById('pf-product-grid');
        if (newGrid && productGrid) productGrid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
        swapEl('pf-pagination', doc);
        history.pushState({ pf: true }, '', nextUrl);
        saveFilterUrl();
        // Prefetch the next page after this one
        setTimeout(prefetchNextPage, 1000);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Load more deals';
      });
  });

  // ─────────────────────────────────────────────
  //  LOADING STATE
  // ─────────────────────────────────────────────
  function setLoading(on) {
    document.body.classList.toggle('pf-is-loading', on);
  }

  // ─────────────────────────────────────────────
  //  BACK / FORWARD navigation
  // ─────────────────────────────────────────────
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.pf) fetchSwap(window.location.href);
  });

  // ── Debounce utility (matches pattern in other pf files) ──
  function debounce(fn, delay) {
    var timer;
    return function () { clearTimeout(timer); timer = setTimeout(fn, delay); };
  }

})();